import { useFormik } from "formik";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FrontendNotification } from "../../../../../../shared/Notification";
import InputCustom from "../../../../../../components/InputCustom";
import SelectCustom from "../../../../../../components/SelectCustom";
import Loading from "../../../../../../core/common/Loading";
import api from "../../../../../../services/api";
import { IUsuario } from "../types/types";
import formValidator from "../validators/formValidator";
import { useAuth } from "../../../../../../hooks/AuthenticateContext";

interface FormValues {
  nome: string;
  email: string;
  cpf: string;
  ativo: boolean;
  id_grupo_patio?: number;
  id_grupo_motorista?: number;
  id_motorista?: number;
  id_grupo_terminal?: number;
  id_terminal?: number;
  id_grupo_transportadora?: number;
  id_transportadora?: number;
  id_grupo_proprietario_carga?: number;
  id_proprietario_carga?: number;
  id_grupo_porto?: number;
}

interface Props {
  isView?: boolean;
  isEdit?: boolean;
  selectedRow?: IUsuario;
  onConfirm: () => void;
  onClose: () => void;
}

const Form: React.FC<Props> = (props: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const { user } = useAuth();
  const [grupos, setGrupos] = useState<any[]>([]);
  const [motoristas] = useState<any[]>([]);
  const [terminais, setTerminais] = useState<any[]>([]);
  const [transportadoras, setTransportadoras] = useState<any[]>([]);
  const [proprietarios, setProprietarios] = useState<any[]>([]);
  const [userData, setUserData] = useState<any>(null);
  const fetchedData = React.useRef(false);
  const lastFetchedId = React.useRef<number | null>(null);

  useEffect(() => {
    if (props.isEdit && props.selectedRow?.id_usuario) {
      if (lastFetchedId.current === props.selectedRow.id_usuario) return;
      
      lastFetchedId.current = props.selectedRow.id_usuario;
      setLoading(true);
      api.post('/listar/usuarios', { 
        id_usuario: props.selectedRow.id_usuario,
        qtd_por_pagina: 1,
        order_by: 'id_usuario',
        order_direction: 'ASC'
      })
        .then((res) => {
           const data = res.data?.data?.[0] || res.data?.[0] || res.data;
           console.log('User Data Loaded:', data);
           setUserData(data);
           setLoading(false);
        })
        .catch((err) => {
           console.error(err);
           setLoading(false);
           lastFetchedId.current = null;
        });
    }
  }, [props.isEdit, props.selectedRow]);

  useEffect(() => {
    if (fetchedData.current) return;
    fetchedData.current = true;

    async function fetchData() {
      try {
        setLoading(true);
        
        const commonBody = {
            qtd_por_pagina: 1000,
            order_direction: 'ASC'
        };

        // Carregar apenas grupos inicialmente
        const gruposRes = await api.post('/listar/grupos', { ...commonBody, order_by: 'id_grupo' });
        setGrupos(gruposRes.data?.data || gruposRes.data || []);
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data", error);
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const loadMotoristas = useCallback(async (inputValue: string) => {
      if (inputValue.length < 3) return [];
      try {
          const response = await api.post('/listar/motoristas', {
              qtd_por_pagina: 20,
              order_by: 'nome',
              order_direction: 'ASC',
              nome: inputValue
          });
          const data = response.data?.data || response.data || [];
          return data.map((m: any) => ({ value: m.id_motorista, label: m.nome }));
      } catch (error) {
          console.error("Error loading motoristas", error);
          return [];
      }
  }, []);

  const fetchEntities = useCallback(async (type: string) => {
      const commonBody = {
          qtd_por_pagina: 99999,
          order_direction: 'ASC'
      };
      
      let promise = null;

      if (type === 'TERMINAL' && terminais.length === 0) {
          promise = api.post('/listar/terminais', { ...commonBody, order_by: 'id_terminal' })
            .then(res => setTerminais(res.data?.data || res.data || []));
      } else if (type === 'TRANSPORTADORA' && transportadoras.length === 0) {
          promise = api.post('/listar/transportadoras', { ...commonBody, order_by: 'id_transportadora' })
            .then(res => setTransportadoras(res.data?.data || res.data || []));
      } else if (type === 'PROPRIETARIO_CARGA' && proprietarios.length === 0) {
          promise = api.post('/listar/proprietariosCarga', { ...commonBody, order_by: 'id_proprietario_carga' })
            .then(res => setProprietarios(res.data?.data || res.data || []));
      }

      if (promise) {
          setLoading(true);
          try {
              await promise;
          } catch (error) {
              console.error("Error fetching entities for " + type, error);
          } finally {
              setLoading(false);
          }
      }
  }, [terminais.length, transportadoras.length, proprietarios.length]);

  const getGruposBySubsistema = (subsistema: string) => {
    return grupos
      .filter((g: any) => g.subsistema === subsistema || !g.subsistema) // Show if match or if subsistema is missing (fallback)
      .map((g: any) => ({ value: g.id_grupo, label: g.nome || g.descricao || "Sem Nome" }));
  };



  const initialValues: FormValues = useMemo(() => {
    const data = userData || props.selectedRow;
    if ((props.isView || props.isEdit) && data) {
      const findGroupId = (sub: string) => {
        if (Array.isArray(data.grupos_usuarios)) {
           // eslint-disable-next-line @typescript-eslint/no-explicit-any
           return data.grupos_usuarios.find((g: any) => g.grupos?.subsistema === sub)?.id_grupo;
        }
        return data.grupos_usuarios?.grupos?.subsistema === sub ? data.grupos_usuarios?.id_grupo : undefined;
     };

     const findEntityId = (data: any, entityKey: string, idKey: string) => {
        if (data[idKey]) return data[idKey];
        if (data[entityKey]) {
            if (Array.isArray(data[entityKey])) {
                return data[entityKey][0]?.[idKey];
            }
            return data[entityKey]?.[idKey];
        }
        // Tenta procurar em chaves compostas comuns
        const pluralKey = entityKey + 's'; // ex: motoristas
        if (data[pluralKey]) {
             if (Array.isArray(data[pluralKey])) {
                return data[pluralKey][0]?.[idKey];
            }
        }
        // Tenta procurar em chaves de relacionamento usuario_entidade
        const relKey = 'usuarios_' + pluralKey; // ex: usuarios_motoristas
        if (data[relKey]) {
             if (Array.isArray(data[relKey])) {
                return data[relKey][0]?.[idKey];
            }
        }
        return undefined;
     };

     const findEntityIdFromGroups = (sub: string, idKey: string) => {
        const groups = data.grupos_usuarios;
        if (!groups) return undefined;
        const groupsArray = Array.isArray(groups) ? groups : [groups];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const groupUser = groupsArray.find((g: any) => g.grupos?.subsistema === sub);
        return groupUser ? groupUser[idKey] : undefined;
     };

      return {
        nome: data.nome || "",
        email: data.email || "",
        cpf: data.cpf || "",
        ativo: data.ativo !== undefined ? data.ativo : true,
        id_grupo_patio: data.id_grupo_patio || findGroupId('PATIO'),
        id_grupo_motorista: data.id_grupo_motorista || findGroupId('MOTORISTA'),
        id_motorista: findEntityId(data, 'motorista', 'id_motorista') || findEntityIdFromGroups('MOTORISTA', 'id_motorista'),
        id_grupo_terminal: data.id_grupo_terminal || findGroupId('TERMINAL'),
        id_terminal: findEntityId(data, 'terminal', 'id_terminal') || findEntityIdFromGroups('TERMINAL', 'id_terminal'),
        id_grupo_transportadora: data.id_grupo_transportadora || findGroupId('TRANSPORTADORA'),
        id_transportadora: findEntityId(data, 'transportadora', 'id_transportadora') || findEntityIdFromGroups('TRANSPORTADORA', 'id_transportadora'),
        id_grupo_proprietario_carga: data.id_grupo_proprietario_carga || findGroupId('PROPRIETARIO_CARGA'),
        id_proprietario_carga: findEntityId(data, 'proprietario_carga', 'id_proprietario_carga') || findEntityIdFromGroups('PROPRIETARIO_CARGA', 'id_proprietario_carga'),
        id_grupo_porto: data.id_grupo_porto || findGroupId('PORTO'),
      } as FormValues;
    }
    return {
      nome: "",
      email: "",
      cpf: "",
      ativo: true,
      id_grupo_patio: undefined,
      id_grupo_motorista: undefined,
      id_motorista: undefined,
      id_grupo_terminal: undefined,
      id_terminal: undefined,
      id_grupo_transportadora: undefined,
      id_transportadora: undefined,
      id_grupo_proprietario_carga: undefined,
      id_proprietario_carga: undefined,
      id_grupo_porto: undefined,
    };
  }, [props.isView, props.isEdit, props.selectedRow, userData]);

  const [selectedGroups, setSelectedGroups] = useState<any[]>([]);
  const [isAddingGroup, setIsAddingGroup] = useState<boolean>(false);
  const [newGroupType, setNewGroupType] = useState<string>("");
  const [newGroupId, setNewGroupId] = useState<number | undefined>(undefined);
  const [newEntityId, setNewEntityId] = useState<number | undefined>(undefined);
  const [newEntityLabel, setNewEntityLabel] = useState<string>("");

  const subsistemaConfig: any = useMemo(() => ({
    PATIO: { label: 'Pátio', groupField: 'id_grupo_patio' },
    MOTORISTA: { 
        label: 'Motorista', 
        groupField: 'id_grupo_motorista', 
        entityField: 'id_motorista', 
        entityLabel: 'Motorista', 
        entityData: motoristas, 
        entityIdKey: 'id_motorista', 
        entityNameKey: 'nome',
        options: motoristas.map((m: any) => ({ value: m.id_motorista, label: m.nome })),
        isAsync: true,
        loadOptions: loadMotoristas
    },
    TERMINAL: { 
        label: 'Terminal', 
        groupField: 'id_grupo_terminal', 
        entityField: 'id_terminal', 
        entityLabel: 'Terminal', 
        entityData: terminais, 
        entityIdKey: 'id_terminal', 
        entityNameKey: 'nome',
        options: terminais.map((t: any) => ({ value: t.id_terminal, label: t.razao_social || t.nome_fantasia || t.nome || "Sem Nome" }))
    },
    TRANSPORTADORA: { 
        label: 'Transportadora', 
        groupField: 'id_grupo_transportadora', 
        entityField: 'id_transportadora', 
        entityLabel: 'Transportadora', 
        entityData: transportadoras, 
        entityIdKey: 'id_transportadora', 
        entityNameKey: 'razao_social',
        options: transportadoras.map((t: any) => ({ value: t.id_transportadora, label: t.razao_social || t.nome_fantasia || t.nome || "Sem Nome" }))
    },
    PROPRIETARIO_CARGA: { 
        label: 'Proprietário Carga', 
        groupField: 'id_grupo_proprietario_carga', 
        entityField: 'id_proprietario_carga', 
        entityLabel: 'Proprietário Carga', 
        entityData: proprietarios, 
        entityIdKey: 'id_proprietario_carga', 
        entityNameKey: 'nome',
        options: proprietarios.map((p: any) => ({ value: p.id_proprietario_carga, label: p.razao_social || p.nome_fantasia || p.nome || "Sem Nome" }))
    },
    PORTO: { label: 'Porto', groupField: 'id_grupo_porto' },
  }), [motoristas, terminais, transportadoras, proprietarios, loadMotoristas]);

  useEffect(() => {
      if (newGroupType) {
          fetchEntities(newGroupType);
      }
  }, [newGroupType, fetchEntities]);

  useEffect(() => {
      if (selectedGroups.length > 0) {
          selectedGroups.forEach(g => fetchEntities(g.type));
      }
  }, [selectedGroups, fetchEntities]);

  useEffect(() => {
      if (initialValues && selectedGroups.length === 0 && (props.isEdit || props.isView)) {
          const loadedGroups: any[] = [];
          Object.keys(subsistemaConfig).forEach(key => {
              const conf = subsistemaConfig[key];
              // @ts-ignore
              const groupId = initialValues[conf.groupField];
              // @ts-ignore
              const entityId = conf.entityField ? initialValues[conf.entityField] : undefined;

              if (groupId) {
                  let entityName = undefined;
                  if (conf.entityField && userData) {
                      const findName = (data: any, key: string, nameKey: string) => {
                          if (data[key]) return Array.isArray(data[key]) ? data[key][0]?.[nameKey] : data[key]?.[nameKey];
                          const plural = key + 's';
                          if (data[plural]) return Array.isArray(data[plural]) ? data[plural][0]?.[nameKey] : data[plural]?.[nameKey];
                          const rel = 'usuarios_' + plural;
                          if (data[rel] && Array.isArray(data[rel])) {
                              const item = data[rel][0];
                              if (item && item[key]) return item[key][nameKey];
                              if (item && item[nameKey]) return item[nameKey];
                          }
                          return undefined;
                      };
                      
                      const typeToKey: any = {
                          'MOTORISTA': 'motorista',
                          'TERMINAL': 'terminal',
                          'TRANSPORTADORA': 'transportadora',
                          'PROPRIETARIO_CARGA': 'proprietario_carga'
                      };
                      
                      if (typeToKey[key]) {
                          entityName = findName(userData, typeToKey[key], conf.entityNameKey);
                      }
                  }

                  loadedGroups.push({
                      type: key,
                      groupId: groupId,
                      entityId: entityId,
                      entityName: entityName
                  });
              }
          });
          // Only set if we found something, to avoid infinite loops or overwrites if empty
          if (loadedGroups.length > 0) {
            setSelectedGroups(loadedGroups);
          }
      }
  }, [initialValues, subsistemaConfig, props.isEdit, props.isView, userData]);

  const handleAddGroup = () => {
      if (!newGroupType || !newGroupId) return;
      
      const conf = subsistemaConfig[newGroupType];
      if (conf.entityField && !newEntityId) {
          alert(`Selecione um(a) ${conf.entityLabel}`);
          return;
      }

      // Check if type already exists
      if (selectedGroups.some(g => g.type === newGroupType)) {
          alert(`Já existe um grupo do tipo ${conf.label} adicionado.`);
          return;
      }

      setSelectedGroups([...selectedGroups, {
          type: newGroupType,
          groupId: newGroupId,
          entityId: newEntityId,
          entityName: newEntityLabel
      }]);

      // Reset fields
      setNewGroupType("");
      setNewGroupId(undefined);
      setNewEntityId(undefined);
      setNewEntityLabel("");
      setIsAddingGroup(false);
  };

  const handleRemoveGroup = (index: number) => {
      const newGroups = [...selectedGroups];
      newGroups.splice(index, 1);
      setSelectedGroups(newGroups);
  };

  const getGroupName = (id: number) => {
      const g = grupos.find((x: any) => x.id_grupo === id);
      return g ? (g.nome || g.descricao) : '---';
  };

  const getEntityName = (type: string, id: number) => {
      const conf = subsistemaConfig[type];
      if (!conf || !conf.entityData) return '---';
      const entity = conf.entityData.find((x: any) => x[conf.entityIdKey] === id);
      return entity ? entity[conf.entityNameKey] : '---';
  };

  const handleSubmit = useCallback(async (values: FormValues, row?: IUsuario) => {
    try {
      setLoading(true);
      
      const body: any = {
        id_configuracao_usuario: row?.id_usuario,
        nome: values.nome,
        email: values.email.toLowerCase(),
        cpf: values.cpf.replace(/\D/g, ""),
        ativo: values.ativo,
        id_usuario_historico: user?.id,
        id_grupo_patio: null,
        id_grupo_motorista: null,
        id_motorista: null,
        id_grupo_terminal: null,
        id_terminal: null,
        id_grupo_transportadora: null,
        id_transportadora: null,
        id_grupo_proprietario_carga: null,
        id_proprietario_carga: null,
        id_grupo_porto: null,
      };

      // Fill from selectedGroups
      selectedGroups.forEach(g => {
          const conf = subsistemaConfig[g.type];
          if (conf) {
              body[conf.groupField] = g.groupId;
              if (conf.entityField && g.entityId) {
                  body[conf.entityField] = g.entityId;
              }
          }
      });

      if (props.isEdit) {
        await api.post("/editar/usuarios", body);
      } else {
        await api.post("/cadastrar/usuarios", body);
      }

      setLoading(false);
      props.onConfirm();
    } catch (error: any) {
      setLoading(false);
      console.error("Erro ao salvar usuário:", error);
      const errorMsg = error.response?.data?.error_msg || error.response?.data?.message || error.message;
      FrontendNotification("Erro ao salvar usuário: " + errorMsg, "error");
    }
  }, [props, selectedGroups, subsistemaConfig]);

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: formValidator,
    onSubmit: (values: FormValues) => handleSubmit(values, props.selectedRow),
  });

  return (
    <>
      <Loading loading={loading} skeletonFields={6} skeletonColumns={1} />
      <div className="p-5" style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
        <div className="mb-4">
            <h2 className="text-lg font-bold mb-2">Identificação</h2>
            <p className="text-sm text-gray-500 mb-4">Dados do usuário utilizado para sua identificação no sistema</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                    <InputCustom
                        id="nome"
                        title="Nome"
                        placeholder="Informe o nome"
                        onChange={formik.handleChange("nome")}
                        touched={formik.touched.nome}
                        error={formik.errors.nome}
                        value={formik.values.nome}
                        disabled={props.isView}
                    />
                </div>
                <div>
                    <InputCustom
                        id="cpf"
                        title="CPF"
                        placeholder="Informe o CPF"
                        onChange={formik.handleChange("cpf")}
                        touched={formik.touched.cpf}
                        error={formik.errors.cpf}
                        value={formik.values.cpf}
                        disabled={props.isView}
                        mask="999.999.999-99"
                        typeInput="mask"
                    />
                </div>
                <div>
                    <InputCustom
                        id="email"
                        title="E-mail"
                        placeholder="Informe o e-mail"
                        onChange={formik.handleChange("email")}
                        touched={formik.touched.email}
                        error={formik.errors.email}
                        value={formik.values.email}
                        disabled={props.isView}
                    />
                </div>
            </div>
        </div>

        <div className="mb-4">
            <h2 className="text-lg font-bold mb-2">Vinculação</h2>
            <p className="text-sm text-gray-500 mb-4">Grupos e empresas que o usuário é vinculado</p>
            
            {/* List of selected groups */}
            <div className="mb-4">
                {selectedGroups.length === 0 && <p className="text-sm text-gray-400 italic">Nenhum grupo vinculado.</p>}
                {selectedGroups.map((group, index) => {
                    const conf = subsistemaConfig[group.type];
                    return (
                        <div key={index} className="flex items-center justify-between bg-white p-3 rounded border border-gray-200 mb-2 shadow-sm">
                            <div className="flex flex-col">
                                <span className="text-xs font-bold text-gray-500 uppercase">{conf?.label}</span>
                                <span className="text-sm font-semibold text-gray-800">{getGroupName(group.groupId)}</span>
                                {conf?.entityField && (
                                    <span className="text-xs text-gray-600 mt-1">
                                        {conf.entityLabel}: {group.entityName || getEntityName(group.type, group.entityId)}
                                    </span>
                                )}
                            </div>
                            {!props.isView && (
                                <button 
                                    type="button"
                                    onClick={() => handleRemoveGroup(index)}
                                    className="text-red-500 hover:text-red-700 p-2"
                                    title="Remover"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Add Group Form */}
            {!props.isView && (
                <div className="mt-4">
                    {!isAddingGroup ? (
                        <button
                            type="button"
                            onClick={() => setIsAddingGroup(true)}
                            className="h-9 pl-4 pr-4 flex items-center justify-center bg-[#0A4984] text-sm text-[#fff] font-bold rounded-full"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                            </svg>
                            Adicionar Grupo
                        </button>
                    ) : (
                        <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                            <h3 className="text-sm font-bold text-gray-700 mb-3">Adicionar Grupo</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <SelectCustom
                                    title="Tipo de Grupo"
                                    data={Object.keys(subsistemaConfig).map(key => ({ value: key, label: subsistemaConfig[key].label }))}
                                    value={newGroupType}
                                    onChange={(e: { value: string }) => {
                                        setNewGroupType(e.value);
                                        setNewGroupId(undefined);
                                        setNewEntityId(undefined);
                                    }}
                                />
                                
                                {newGroupType && (
                                    <SelectCustom
                                        title="Grupo"
                                        data={getGruposBySubsistema(newGroupType)}
                                        value={newGroupId}
                                        onChange={(e: { value: number }) => setNewGroupId(e.value)}
                                    />
                                )}

                                {newGroupType && subsistemaConfig[newGroupType].entityField && (
                                    <SelectCustom
                                        title={subsistemaConfig[newGroupType].entityLabel}
                                        data={subsistemaConfig[newGroupType].isAsync ? subsistemaConfig[newGroupType].loadOptions : subsistemaConfig[newGroupType].options}
                                        value={newEntityId}
                                        onChange={(e: any) => {
                                            setNewEntityId(e.value);
                                            setNewEntityLabel(e.label);
                                        }}
                                        async={subsistemaConfig[newGroupType].isAsync}
                                    />
                                )}
                            </div>
                            <div className="mt-3 flex justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsAddingGroup(false);
                                        setNewGroupType("");
                                        setNewGroupId(undefined);
                                        setNewEntityId(undefined);
                                    }}
                                    className="px-4 py-2 rounded-full text-sm font-bold text-gray-600 bg-white border border-gray-300 hover:bg-gray-50"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    onClick={handleAddGroup}
                                    disabled={!newGroupType || !newGroupId || (subsistemaConfig[newGroupType]?.entityField && !newEntityId)}
                                    className={`px-4 py-2 rounded-full text-sm font-bold text-white ${
                                        !newGroupType || !newGroupId || (subsistemaConfig[newGroupType]?.entityField && !newEntityId)
                                        ? 'bg-gray-400 cursor-not-allowed'
                                        : 'bg-[#0A4984] hover:bg-[#083b6b]'
                                    }`}
                                >
                                    Adicionar
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>


      </div>
      <div className="w-full h-14 flex items-center justify-end bg-[#FFFFFF] shadow-xl">
        <button
          type="button"
          className="w-24 h-9 pl-3 pr-3 flex items-center justify-center bg-[#F9FAFA] text-sm text-[#000000] font-bold rounded-full mr-2"
          style={{ border: "1px solid #DBDEDF" }}
          onClick={props.onClose}
          id="btnCancel"
        >
          Cancelar
        </button>
        {!props.isView && (
            <button
            type="button"
            className="w-24 h-9 pl-3 pr-3 flex items-center justify-center bg-[#0A4984] text-sm text-[#fff] font-bold rounded-full mr-2"
            onClick={() => formik.handleSubmit()}
            id="btnSave"
            >
            Salvar
            </button>
        )}
      </div>
    </>
  );
};

export default Form;
